import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';

export type ThemeName = 'light' | 'dark';

function read(key: string): ThemeName {
  try { const v = localStorage.getItem(key); if (v === 'dark' || v === 'light') return v; } catch { /* storage blocked */ }
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Light or dark for a portal: follows the device until the person picks one, then remembers it on this device. */
export function useTheme(key: string): [ThemeName, (t: ThemeName) => void] {
  const [theme, set] = useState<ThemeName>(() => read(key));
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0E0E0E' : '#F5F1EA');
  }, [theme]);
  return [theme, (t) => { try { localStorage.setItem(key, t); } catch { /* ignore */ } set(t); }];
}

type VT = { startViewTransition?: (cb: () => void) => { ready: Promise<void> } };

/** Changes the theme with a circle that grows from the button, where the browser supports it. */
export function switchTheme(next: ThemeName, apply: (t: ThemeName) => void, from?: { x: number; y: number }) {
  const doc = document as unknown as VT;
  const calm = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!doc.startViewTransition || calm) { apply(next); document.documentElement.dataset.theme = next; return; }
  const x = from?.x ?? innerWidth - 40, y = from?.y ?? 40;
  const r = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
  const t = doc.startViewTransition(() => { document.documentElement.dataset.theme = next; apply(next); });
  t.ready.then(() => {
    document.documentElement.animate({ clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
      { duration: 520, easing: 'cubic-bezier(.2,.8,.2,1)', pseudoElement: '::view-transition-new(root)' });
  }).catch(() => undefined);
}

/** The light / dark switch for the top bar: a small track with a sun and a moon. */
export function ThemeToggle({ theme, onChange, labels = { light: 'Light mode', dark: 'Dark mode' } }: { theme: ThemeName; onChange: (t: ThemeName) => void; labels?: { light: string; dark: string } }) {
  const dark = theme === 'dark';
  const next: ThemeName = dark ? 'light' : 'dark';
  return (
    <button type="button" className={`theme-sw${dark ? ' is-dark' : ''}`} role="switch" aria-checked={dark} aria-label={dark ? labels.light : labels.dark} title={dark ? labels.light : labels.dark}
      onClick={(e) => { const b = (e.currentTarget as HTMLElement).getBoundingClientRect(); switchTheme(next, onChange, { x: b.left + b.width / 2, y: b.top + b.height / 2 }); }}>
      <span className="theme-sw-ic sun" aria-hidden="true"><Sun /></span>
      <span className="theme-sw-ic moon" aria-hidden="true"><Moon /></span>
      <span className="theme-sw-knob" aria-hidden="true" />
    </button>
  );
}
